import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import flag from "../../assets/flag.png";
import check from "../../assets/check.png";
import {
  EligibilityFormOption,
  EligibilityFormQuestion,
  fetchVisaQuestions,
  VisaType,
  createApplication,
  submitEligibilityResponses,
} from "../../API/visaEligibilityAPI.ts";
import { GoInfo } from "react-icons/go";
import { FaLock } from "react-icons/fa6";
import AlertsModal from "../AlertsModal.tsx";
import arrow from "../../assets/right-arrow.png";
import TipsModal from "../TipsModal.tsx";
import { getUserProfile } from "../../API/profileAPI.ts";
import { FaExclamationTriangle } from "react-icons/fa";
import useSignedIn from "../../hooks/useSignedIn";
import ButtonLoadingLabel from "../ButtonLoadingLabel";
import GuidanceSections from "../GuidanceSections";

const buildEligibilitySessionKey = (visaType: VisaType): string =>
  `eligibility:${visaType}:${Date.now()}`;

export enum VisaEligibilityStepStatus {
  NOT_STARTED = "NOT_STARTED",
  BENEFICIARY_SELECTION = "BENEFICIARY_SELECTION",
  STARTED = "STARTED",
  COMPLETED = "COMPLETED",
}

const VisaEligibilityStep = () => {
  const navigate = useNavigate();
  const { visaType } = useParams<{ visaType: VisaType }>();
  const [hasStarted, setHasStarted] = useState<VisaEligibilityStepStatus>(
    VisaEligibilityStepStatus.NOT_STARTED
  );
  // Task 149: a non-credential marker, not a token. Subscribed, so an
  // expired session discovered mid-flow updates this step instead of leaving
  // it offering actions the API will refuse.
  const isLoggedIn = useSignedIn();

  // Get user's full name from localStorage
  const getUserFullName = async (): Promise<string | null> => {
    if (isLoggedIn) {
      // Let's call User Profile API to get the user's full name
      const userProfile = await getUserProfile();

      console.log("User profile:", userProfile);
      return userProfile?.data?.data?.fullName ?? null;
    }
    return null;
  };

  const [questions, setQuestions] = useState<EligibilityFormQuestion[]>([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [formAnswers, setFormAnswers] = useState<{
    [key: EligibilityFormQuestion["id"]]: number | number[];
  }>({});
  const [userQuestionHistoryStack, setUserQuestionHistoryStack] = useState<
    string[]
  >([]);
  const [showAlert, setShowAlert] = useState<string | null>(null);
  const [showTipModal, setShowTipModal] = useState(false);
  const [showTipModalOption, setShowTipModalOption] =
    useState<EligibilityFormOption | null>(null);
  const [isCompletingEligibility, setIsCompletingEligibility] = useState(false);
  const [pendingRedirectToReview, setPendingRedirectToReview] = useState(false);
  const terminalCompletionRef = useRef(false);
  // Initialize beneficiaryName and beneficiaryType from localStorage if available
  const [beneficiaryName, setBeneficiaryName] = useState<string>(() => {
    return localStorage.getItem("beneficiaryName") || "";
  });
  const [beneficiaryType, setBeneficiaryType] = useState<"self" | "other">(
    (() => {
      const stored = localStorage.getItem("beneficiaryType");
      return (stored === "other" ? "other" : "self") as "self" | "other";
    })()
  );

  const getConfig = useCallback(
    async (visaType: VisaType) => {
      const response = await fetchVisaQuestions(visaType);
      setQuestions(response);
    },
    [visaType]
  );

  const handleTip = (option: EligibilityFormOption) => {
    setShowTipModalOption(option);
  };

  /**
   * Transform answers to API format for submission
   */
  const transformAnswersToAPIFormat = (
    parsedAnswers: { [key: number]: number | number[] },
    questions: EligibilityFormQuestion[]
  ) => {
    const responses: Array<{
      questionId: number;
      optionId: number;
      answerText: string;
      explanation: string | null;
      isReview: boolean;
    }> = [];

    Object.entries(parsedAnswers).forEach(([questionIdStr, answerId]) => {
      const questionId = parseInt(questionIdStr);
      const question = questions.find((q) => q.id === questionId);

      if (!question) return;

      if (question.multiSelect && Array.isArray(answerId)) {
        answerId.forEach((optId) => {
          const option = question.options?.find((o) => o.id === optId);
          if (option) {
            responses.push({
              questionId,
              optionId: optId,
              answerText: option.text,
              explanation: option.explanation || null,
              isReview: false,
            });
          }
        });
      } else if (typeof answerId === "number") {
        const option = question.options?.find((o) => o.id === answerId);
        if (option) {
          responses.push({
            questionId,
            optionId: answerId,
            answerText: option.text,
            explanation: option.explanation || null,
            isReview: false,
          });
        }
      }
    });

    return responses;
  };

  /**
   * Save eligibility responses to backend immediately when form completes
   * This ensures failed eligibility checks are captured even if user closes browser
   */
  const saveEligibilityToBackend = async (idempotencyKey: string) => {
    if (!isLoggedIn || !visaType) return;

    try {
      // Get beneficiary name from localStorage
      let beneficiaryName = localStorage.getItem("beneficiaryName");

      // If not found, try to get from user details
      if (!beneficiaryName) {
        beneficiaryName = await getUserFullName();
      }

      // If still no name, skip backend save (shouldn't happen but safety check)
      if (!beneficiaryName) {
        console.warn("No beneficiary name found, skipping backend save");
        return;
      }

      console.log("Saving eligibility to backend...", {
        beneficiaryName,
        visaType,
      });

      // Create application
      const application = await createApplication(
        beneficiaryName,
        visaType as VisaType,
        { idempotencyKey }
      );
      console.log("Application created:", application);

      if (application.stage !== "eligibility_check") {
        localStorage.setItem("currentApplicationId", application.id.toString());
        localStorage.setItem("isQualified", String(application.isQualified));
        return {
          application,
          responses: [],
          isQualified: Boolean(application.isQualified),
        };
      }

      // Transform and submit responses
      const apiResponses = transformAnswersToAPIFormat(formAnswers, questions);
      const result = await submitEligibilityResponses(
        application.id,
        apiResponses
      );
      console.log("Eligibility saved:", result);

      // Store application details for later use
      localStorage.setItem("currentApplicationId", application.id.toString());
      localStorage.setItem("isQualified", result.isQualified.toString());

      return result;
    } catch (error) {
      console.error("Error saving eligibility to backend:", error);
      // Don't block the user flow, just log the error
      return undefined;
    }
  };

  const handleStartClick = () => {
    // Show beneficiary selection screen for this specific form
    setHasStarted(VisaEligibilityStepStatus.BENEFICIARY_SELECTION);
  };

  const handleBeneficiarySubmit = async () => {
    if (beneficiaryType === "self" && isLoggedIn) {
      const userFullName = await getUserFullName();
      localStorage.setItem("beneficiaryName", userFullName ?? "");
      localStorage.setItem("beneficiaryType", "self");
      setBeneficiaryName(userFullName ?? "");
      setHasStarted(VisaEligibilityStepStatus.STARTED);
    } else if (beneficiaryType === "self" && !isLoggedIn) {
      setShowAlert(null);
      localStorage.setItem("beneficiaryType", "self");
      setHasStarted(VisaEligibilityStepStatus.STARTED);
    } else if (beneficiaryType === "other") {
      if (!beneficiaryName.trim()) {
        console.log("beneficiaryName is empty");
        setShowAlert("Please enter the beneficiary's name.");
        return;
      } else {
        setShowAlert(null);
      }
      localStorage.setItem("beneficiaryName", beneficiaryName.trim());
      localStorage.setItem("beneficiaryType", "other");
      setBeneficiaryName(beneficiaryName.trim());
      setHasStarted(VisaEligibilityStepStatus.STARTED);
    }
  };

  const handleBack = () => {
    if (currentStep === 1 && isLoggedIn) {
      // Go back to beneficiary selection if on first question and logged in
      setHasStarted(VisaEligibilityStepStatus.BENEFICIARY_SELECTION);
    } else if (currentStep > 1 && userQuestionHistoryStack.length > 0) {
      const previousQuestionIdx = questions.findIndex(
        (q) =>
          q.id ===
          Number(userQuestionHistoryStack[userQuestionHistoryStack.length - 1])
      );
      setUserQuestionHistoryStack(userQuestionHistoryStack.slice(0, -1));
      setCurrentStep(previousQuestionIdx + 1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = async (alert: boolean) => {
    if (terminalCompletionRef.current) return;

    const currentQuestion = questions[currentStep - 1];
    const selectedOption = formAnswers[currentQuestion.id];

    if (!selectedOption) return;

    if (currentQuestion.multiSelect) {
      const selectedOptions = selectedOption as number[];
      if (selectedOptions.length === 0) return;

      const selectedOptionObjects =
        currentQuestion.options?.filter((opt) =>
          selectedOptions.includes(opt.id)
        ) || [];

      const firstExplanation = selectedOptionObjects.find(
        (opt) => opt.explanation
      );

      if (firstExplanation?.explanation && !alert) {
        setShowAlert(firstExplanation.explanation || null);
      } else {
        setShowAlert(null);
        if (currentQuestion.isTerminal) {
          terminalCompletionRef.current = true;
          setIsCompletingEligibility(true);
          const eligibilitySessionKey = buildEligibilitySessionKey(
            visaType as VisaType
          );

          // ** Let's save the response to localStorage.
          localStorage.setItem(
            "visaEligibilityResponses",
            JSON.stringify({
              [eligibilitySessionKey]: formAnswers,
            })
          );
          // ** Let's save the visaType to localStorage.
          localStorage.setItem("latestVisaType", visaType as VisaType);
          // ** Let's save the userQuestionHistoryStack to localStorage.
          localStorage.setItem(
            "userQuestionHistoryStack",
            JSON.stringify(userQuestionHistoryStack)
          );

          setUserQuestionHistoryStack([
            ...userQuestionHistoryStack,
            currentQuestion.id.toString(),
          ]);

          // ** Save to backend immediately for logged-in users (including failed eligibility)
          if (isLoggedIn) {
            await saveEligibilityToBackend(eligibilitySessionKey);
            setPendingRedirectToReview(true);
          }

          setHasStarted(VisaEligibilityStepStatus.COMPLETED);
          setIsCompletingEligibility(false);

          return;
        } else {
          setUserQuestionHistoryStack([
            ...userQuestionHistoryStack,
            currentQuestion.id.toString(),
          ]);

          let nextQuestion: EligibilityFormQuestion | undefined;
          for (const option of selectedOptionObjects) {
            if (
              option.nextQuestions?.length &&
              option.nextQuestions.length > 0
            ) {
              nextQuestion = option.nextQuestions[0];
              break;
            }
          }

          if (nextQuestion) {
            setCurrentStep(
              (questions.findIndex((q) => q.id === nextQuestion!.id) +
                1) as number
            );
          } else {
            setCurrentStep(currentStep + 1);
          }

          setTimeout(() => {
            const mainElement = document.querySelector("main");
            if (mainElement) {
              mainElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 80);
        }
      }
    } else {
      const selectedOptionObject: EligibilityFormOption | undefined =
        currentQuestion.options?.find(
          (opt) => String(opt.id) === String(selectedOption)
        );

      if (selectedOptionObject?.explanation && !alert) {
        setShowAlert(selectedOptionObject.explanation || null);
      } else {
        setShowAlert(null);
        if (currentQuestion.isTerminal) {
          terminalCompletionRef.current = true;
          setIsCompletingEligibility(true);
          const eligibilitySessionKey = buildEligibilitySessionKey(
            visaType as VisaType
          );

          // ** Let's save the response to localStorage.
          localStorage.setItem(
            "visaEligibilityResponses",
            JSON.stringify({
              [eligibilitySessionKey]: formAnswers,
            })
          );
          localStorage.setItem("latestVisaType", visaType as VisaType);
          // ** Let's save the userQuestionHistoryStack to localStorage.
          localStorage.setItem(
            "userQuestionHistoryStack",
            JSON.stringify(userQuestionHistoryStack)
          );

          setUserQuestionHistoryStack([
            ...userQuestionHistoryStack,
            currentQuestion.id.toString(),
          ]);

          // ** Save to backend immediately for logged-in users (including failed eligibility)
          if (isLoggedIn) {
            await saveEligibilityToBackend(eligibilitySessionKey);
            setPendingRedirectToReview(true);
          }

          setHasStarted(VisaEligibilityStepStatus.COMPLETED);
          setIsCompletingEligibility(false);

          return;
        } else {
          setUserQuestionHistoryStack([
            ...userQuestionHistoryStack,
            currentQuestion.id.toString(),
          ]);
          if (
            selectedOptionObject?.nextQuestions?.length &&
            selectedOptionObject.nextQuestions.length > 0
          ) {
            const nextQuestion: EligibilityFormQuestion =
              selectedOptionObject.nextQuestions[0];
            setCurrentStep(
              (questions.findIndex((q) => q.id === nextQuestion.id) +
                1) as number
            );
          } else {
            setUserQuestionHistoryStack([
              ...userQuestionHistoryStack,
              currentQuestion.id.toString(),
            ]);
            setCurrentStep(currentStep + 1);
          }

          setTimeout(() => {
            const mainElement = document.querySelector("main");
            if (mainElement) {
              mainElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 100);
        }
      }
    }
  };

  useEffect(() => {
    if (visaType) {
      getConfig(visaType as VisaType);

      // Only clear beneficiary data if we're starting fresh (not already started)
      // This prevents clearing data when component re-renders during the eligibility check
      if (hasStarted === VisaEligibilityStepStatus.NOT_STARTED) {
        localStorage.removeItem("beneficiaryName");
        localStorage.removeItem("beneficiaryType");
        setBeneficiaryName("");
        setBeneficiaryType("self");
      }
    }
  }, [getConfig, visaType, hasStarted]);

  // Redirect logged-in users directly to AnswerReview when form is completed
  useEffect(() => {
    if (pendingRedirectToReview && hasStarted === VisaEligibilityStepStatus.COMPLETED && isLoggedIn) {
      navigate("/answer-review");
    }
  }, [hasStarted, isLoggedIn, navigate, pendingRedirectToReview]);

  if (!questions.length) {
    return (
      <div className="relative login_main body_padding pb-[100px] animate-pulse">
        <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4">
          <div className="w-full bg-white shadow-md rounded-2xl p-[20px] md:p-[50px] text-center">
            <img
              src={flag}
              alt="US Flag"
              className="w-[80px] h-[80px] mx-auto mb-6 rounded-full"
            />
            <h1 className="mb-0 text-center sm:mb-1">
              Checking your Visa Eligibility..
            </h1>
            <p className="text-[#1B365D] mb-6 text-center mt-5 text-lg">
              Please wait while we load your questions.
            </p>
            <button
              disabled
              className="bg-[#F4A261] text-[#1B365D] font-semibold px-[40px] py-4 rounded-[50px] text-xl"
            >
              <ButtonLoadingLabel label="Loading" />
            </button>
          </div>
        </div>
        <p className="text-base text-[#1B365D] mt-6 text-center">
          Need help? Chat with us
        </p>
      </div>
    );
  }

  if (hasStarted === VisaEligibilityStepStatus.NOT_STARTED) {
    const visaTitles = {
      "N-400": "Citizenship Application",
      "I-90": "Green Card Renewal/Replacement",
      "I-751": "Green Card Conditions Removal",
      "I-130": "Family Sponsorship",
      "I-131": "Travel Document Application",
      "n-400": "Citizenship Application",
      "i-90": "Green Card Renewal/Replacement",
      "i-751": "Green Card Conditions Removal",
      "i-130": "Family Sponsorship",
      "i-131": "Travel Document Application",
    };

    return (
      <div className="relative login_main body_padding pb-[100px]">
        <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4">
          <div className="w-full bg-white shadow-md rounded-2xl p-[20px] md:p-[50px] text-center">
            <img
              src={flag}
              alt="US Flag"
              className="w-[80px] h-[80px] mx-auto mb-6 rounded-full"
            />
            <h1 className="mb-0 text-center sm:mb-1">
              Let&apos;s see if you&apos;re eligible to apply for{" "}
              {visaTitles[visaType as VisaType] || "this Visa"}.
            </h1>
            <p className="text-[#1B365D] mb-6 text-center mt-5 text-lg">
              Please answer a few quick questions to find out.
            </p>
            <button
              onClick={handleStartClick}
              className="bg-[#F4A261] text-[#1B365D] font-semibold px-[40px] py-4 rounded-[50px] text-xl hover:bg-[#F29449]"
            >
              Start Now
            </button>
          </div>
        </div>
        <p className="text-base text-[#1B365D] mt-6 text-center">
          Need help? Chat with us
        </p>
      </div>
    );
  }

  if (hasStarted === VisaEligibilityStepStatus.BENEFICIARY_SELECTION) {
    return (
      <div className="relative login_main body_padding pb-[100px]">
        <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4">
          <div className="w-full bg-white shadow-md rounded-2xl p-[20px] md:p-[50px]">
            <img
              src={flag}
              alt="US Flag"
              className="w-[80px] h-[80px] mx-auto mb-6 rounded-full"
            />
            <h1 className="mb-0 text-center sm:mb-6">
              Who is this {visaType} Eligibility Check for?
            </h1>

            <div className="space-y-4 mb-6 mt-8">
              <label className="flex items-center p-4 border-2 rounded-xl cursor-pointer hover:border-[#F4A261] transition-colors">
                <input
                  type="radio"
                  name="beneficiary-type"
                  value="self"
                  checked={beneficiaryType === "self"}
                  onChange={() => setBeneficiaryType("self")}
                  className="mr-4 h-5 w-5 text-[#F4A261]"
                  style={{ accentColor: "#F4A261" }}
                />
                <span className="text-lg text-[#1B365D]">Myself</span>
              </label>

              <label className="flex items-center p-4 border-2 rounded-xl cursor-pointer hover:border-[#F4A261] transition-colors">
                <input
                  type="radio"
                  name="beneficiary-type"
                  value="other"
                  checked={beneficiaryType === "other"}
                  onChange={() => setBeneficiaryType("other")}
                  className="mr-4 h-5 w-5 text-[#F4A261]"
                  style={{ accentColor: "#F4A261" }}
                />
                <span className="text-lg text-[#1B365D]">
                  Someone else (Beneficiary)
                </span>
              </label>
            </div>

            {beneficiaryType === "other" && (
              <>
                <div className="mb-2">
                  <label className="block text-sm font-semibold mb-2 text-[#1B365D] text-left">
                    Beneficiary's Full Name{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={beneficiaryName}
                    onChange={(e) => setBeneficiaryName(e.target.value)}
                    placeholder="Enter full name"
                    className="w-full p-3 border-2 rounded-xl focus:border-[#F4A261] focus:outline-none"
                    autoFocus
                  />
                </div>
                {showAlert && (
                  <p className="text-sm text-[#1B365D] text-left text-red-500 my-2">
                    {showAlert}
                  </p>
                )}
              </>
            )}

            <div className="flex gap-4 mt-8">
              <button
                onClick={() =>
                  setHasStarted(VisaEligibilityStepStatus.NOT_STARTED)
                }
                className="flex-1 py-3 md:py-4 border-2 border-[#1B365D] text-[#1B365D] rounded-full font-semibold hover:bg-[#1B365D] hover:text-white transition-colors text-lg md:text-xl"
              >
                Back
              </button>
              <button
                onClick={handleBeneficiarySubmit}
                className="flex-1 py-3 md:py-4 bg-[#F4A261] text-[#1B365D] rounded-full font-semibold hover:bg-[#F29449] transition-colors text-lg md:text-xl"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
        <p className="text-base text-[#1B365D] mt-6 text-center">
          Need help? Chat with us
        </p>
      </div>
    );
  }

  if (hasStarted === VisaEligibilityStepStatus.COMPLETED) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-start py-10 px-4 pt-[200px]">
        <div className="max-w-2xl mx-auto w-full mb-6"></div>
        <div className="w-full max-w-2xl bg-white rounded-xl shadow-md p-5 md:p-8 mt-4 text-center">
          <img
            src={check}
            alt="Check"
            className="w-[80px] h-[80px] mx-auto mb-6 rounded-full"
          />
          <h2 className="text-[28px] text-[#1e2b5e] mb-4 font-georgia">
            Your eligibility test is completed!
          </h2>
          <p className="text-[#1B365D] mb-6 text-center">
            {isLoggedIn
              ? "You can now view your answers and continue your application."
              : "Sign in or create an account to view your results and start your application."}
          </p>

          {isLoggedIn ? (
            <button
              onClick={() => navigate("/answer-review")}
              className="bg-[#F4A261] text-[#1B365D] border-2 border-[#F4A261] font-semibold md:px-[40px] px-[30px] md:py-4 py-2 rounded-[50px] text-xl hover:bg-[#F29449] w-full"
            >
              View Answers
            </button>
          ) : (
            <div className="flex justify-between items-center pt-4">
              <button
                onClick={() => navigate("/")}
                className="bg-[#fff] text-[#1B365D] border-2 border-[#1B365D] font-semibold md:px-[60px] px-[30px] md:py-4 py-2 rounded-[50px] text-xl hover:bg-[#1B365D] hover:text-white"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/sign-up")}
                className="flex items-center bg-[#F4A261] text-[#1B365D] border-2 border-[#F4A261] font-semibold md:px-[40px] px-[30px] md:py-4 py-2 rounded-[50px] text-xl hover:bg-[#F29449]"
              >
                Create Account
                <img src={arrow} alt="arrow" className="ms-3" />
              </button>
            </div>
          )}

          <div className="bg-[#F9FAFB]  shadow-sm p-4 rounded-md text-[#1e2b5e] text-base mt-7 flex items-center">
            <FaLock size={18} />
            <p className="ms-2 text-[16px] leading-[16px]">
              Your responses are saved securely. Creating an account lets you
              access your results and continue your application process. &nbsp;
            </p>
          </div>
        </div>
        <p className="text-base text-[#1B365D] mt-6 text-center">
          Need help? Chat with us
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex flex-col items-center justify-start py-10 px-4 pt-[200px]">
      {/* ** Debugging Information */}
      {/* <div className="absolute bottom-0 left-0 w-full max-w-[30vw] mx-auto flex flex-col items-start px-4 p-5 md:p-8 mt-4">
        <code className="text-sm text-gray-500 mb-2">Debugging</code>
        <h6 className="text-base font-bold">User Question History Stack</h6>
        {userQuestionHistoryStack.map((id) => (
          <p key={id} className="text-sm mb-2">
            <span className="font-bold text-gray-500 me-2">{id}:</span>
            {questions.find((q) => q.id === Number(id))?.questionText}
          </p>
        ))}
        <h6 className="text-base font-bold">Form Answers</h6>
        {Object.entries(formAnswers).map(([id, answer]) => (
          <p key={id} className="text-sm mb-2">
            <span className="font-bold text-gray-500 me-2">{id}:</span>
            {answer}
          </p>
        ))}
      </div> */}
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center px-4">
        <div className="w-full bg-white shadow-md rounded-2xl p-[20px] md:p-[50px] text-center">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-[22px] md:text-[28px] md:leading-[40px] leading-[32px] text-[#1e2b5e]  w-[95%] text-left leading-[100%] tracking-[0px] vertical-align-middle">
              {questions[currentStep - 1].questionText}
            </h1>
            {questions[currentStep - 1]?.suggestedTip && (
              <button
                onClick={() => {
                  setShowTipModal(true);
                }}
              >
                <GoInfo size={18} color="#a0a0a0" />
              </button>
            )}
          </div>
          <div className="space-y-4 mb-6">
            {questions[currentStep - 1].options?.map((opt) => (
              <div className="flex items-center justify-between" key={opt.id}>
                <label
                  className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all duration-200 ${
                    questions[currentStep - 1]?.options?.some((opt) => opt.tips)
                      ? "w-[95%]"
                      : "w-[100%]"
                  }`}
                >
                  <input
                    type={
                      questions[currentStep - 1].multiSelect
                        ? "checkbox"
                        : "radio"
                    }
                    name={`question_${questions[currentStep - 1].id}`}
                    value={opt.id}
                    checked={
                      questions[currentStep - 1].multiSelect
                        ? Array.isArray(
                            formAnswers[questions[currentStep - 1].id]
                          ) &&
                          (
                            formAnswers[
                              questions[currentStep - 1].id
                            ] as number[]
                          ).includes(opt.id)
                        : formAnswers[questions[currentStep - 1].id] === opt.id
                    }
                    onChange={() => {
                      const currentQuestion = questions[currentStep - 1];
                      const currentAnswer = formAnswers[currentQuestion.id];

                      if (currentQuestion.multiSelect) {
                        const currentArray = Array.isArray(currentAnswer)
                          ? currentAnswer
                          : [];
                        const isSelected = currentArray.includes(opt.id);

                        if (isSelected) {
                          const newArray = currentArray.filter(
                            (id) => id !== opt.id
                          );
                          setFormAnswers({
                            ...formAnswers,
                            [currentQuestion.id]: newArray,
                          });
                        } else {
                          setFormAnswers({
                            ...formAnswers,
                            [currentQuestion.id]: [...currentArray, opt.id],
                          });
                        }
                      } else {
                        setFormAnswers({
                          ...formAnswers,
                          [currentQuestion.id]: opt.id,
                        });
                      }
                    }}
                    className={`mt-1 mr-4 h-5 w-5 text-[#1e2b5e] checked:bg-[#1e2b5e] ${
                      questions[currentStep - 1]?.multiSelect
                        ? "border-4 border-[#1e2b5e]"
                        : "border-2 border-[#1e2b5e]"
                    }`}
                    style={{
                      accentColor: "#1e2b5e",
                    }}
                  />
                  <div className="w-[90%]">
                    <p className="font-medium flex items-center gap-2 leading-[100%] tracking-[0%] vertical-align-middle font-nunito">
                      {opt.text}
                    </p>
                  </div>
                </label>
                {opt.tips && (
                  <button
                    type="button"
                    className="ml-2 text-gray-500"
                    onClick={() => {
                      handleTip(opt);
                    }}
                  >
                    <GoInfo size={18} color="#a0a0a0" />
                  </button>
                )}
                {showTipModalOption === opt && (
                  <TipsModal
                    show={showTipModalOption === opt}
                    onClose={() => setShowTipModalOption(null)}
                    tipText={showTipModalOption?.tips || ""}
                  />
                )}
              </div>
            ))}
          </div>
          {(questions[currentStep - 1]?.contextLine1 ||
            questions[currentStep - 1]?.contextLine2) && (
            <div
              /* text-left: the question card centers its contents, which left
                 the bullets hanging away from their centered, ragged text. */
              className="bg-[#f1f2f6] border-l-4 border-[#1e2b5e] shadow-sm p-4 rounded-md text-[#1B365D] text-base text-left"
              style={{ boxShadow: "0px 4px 4px 0px #00000040" }}
            >
              <GuidanceSections
                text={[
                  questions[currentStep - 1]?.contextLine1,
                  questions[currentStep - 1]?.contextLine2,
                ]
                  .filter(Boolean)
                  .join("\n")}
              />
            </div>
          )}
          <div
            className={`flex ${
              currentStep === 1 && !isLoggedIn
                ? "w-full justify-end"
                : "justify-between items-center"
            } mt-6`}
          >
            {(currentStep > 1 || isLoggedIn) && (
            <button
              onClick={handleBack}
              disabled={isCompletingEligibility}
              className="bg-[#fff] text-[#1B365D] border-2 border-[#1B365D] font-semibold md:px-[40px] px-[30px] px-8 py-4 rounded-[50px] text-xl hover:bg-[#1B365D] hover:text-white flex items-center"
            >
              Back
            </button>
            )}
            <button
              onClick={() => handleNext(false)}
              className={`bg-[#F4A261] text-[#1B365D] border-2 border-[#F4A261] font-semibold md:px-[40px] px-[30px] md:py-4 py-2 rounded-[50px] text-xl hover:bg-[#F29449] flex items-center ${
                questions[currentStep - 1].multiSelect
                  ? !Array.isArray(
                      formAnswers[questions[currentStep - 1].id]
                    ) ||
                    (formAnswers[questions[currentStep - 1].id] as number[])
                      .length === 0
                    ? "bg-gray-300 cursor-not-allowed border-gray-300 hover:bg-gray-300 hover:border-gray-300"
                    : "bg-[#F4A261] hover:bg-[#F29449] border-[#F4A261]"
                  : !formAnswers[questions[currentStep - 1].id]
                  ? "bg-gray-300 cursor-not-allowed border-gray-300 hover:bg-gray-300 hover:border-gray-300"
                  : "bg-[#F4A261] hover:bg-[#F29449] border-[#F4A261]"
              }`}
              disabled={
                isCompletingEligibility ||
                (questions[currentStep - 1].multiSelect
                  ? !Array.isArray(
                      formAnswers[questions[currentStep - 1].id]
                    ) ||
                    (formAnswers[questions[currentStep - 1].id] as number[])
                      .length === 0
                  : !formAnswers[questions[currentStep - 1].id])
              }
              style={{
                cursor: questions[currentStep - 1].multiSelect
                  ? !Array.isArray(
                      formAnswers[questions[currentStep - 1].id]
                    ) ||
                    (formAnswers[questions[currentStep - 1].id] as number[])
                      .length === 0
                    ? "not-allowed"
                    : "pointer"
                  : !formAnswers[questions[currentStep - 1].id]
                  ? "not-allowed"
                  : "pointer",
                alignSelf: "flex-end",
              }}
            >
              {isCompletingEligibility ? "Saving..." : "Continue"}
            </button>
          </div>
          <p className="text-base text-[#1B365D] mt-6 text-center">
            Need help? Chat with us
          </p>
        </div>
      </div>
      {showAlert && (
        <AlertsModal
          show={showAlert !== null}
          onClose={() => setShowAlert(null)}
          alertText={showAlert}
          onNext={() => handleNext(true)}
        />
      )}
      {showTipModal && (
        <TipsModal
          show={showTipModal}
          onClose={() => setShowTipModal(false)}
          tipText={questions[currentStep - 1]?.suggestedTip}
        />
      )}
    </div>
  );
};

export default VisaEligibilityStep;
